// FASE 3.5 (3.4) — Curated Lists page (/shadow/listas).
// Index of my lists + create form. Click a list → /shadow/listas/:id.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, List as ListIcon, Lock, Globe, Trash2, Loader2, Crown, AlertCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tFases } from "../i18n/shadow-fases";

export default function ShadowListsPage() {
  const { user, token, openLogin, isPro } = useAuth();
  const { lang } = useLanguage();
  const [lists, setLists] = useState([]);
  const [quota, setQuota] = useState({ used: 0, limit: 3 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", visibility: "public" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const r = await fetch(`${API}/shadow/lists`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const d = await safeJson(r);
    if (r.ok && d) {
      setLists(d.lists || []);
      if (d.quota) setQuota(d.quota);
    }
    setLoading(false);
  }, [user, token]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setErr(null);
    if (form.name.trim().length < 2) { setErr(tFases(lang, "ls_err_name_short")); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/shadow/lists`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(form),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d?.detail?.message || d?.detail || "Error");
      setShowForm(false);
      setForm({ name: "", description: "", visibility: "public" });
      await load();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(tFases(lang, "ls_confirm_delete"))) return;
    await fetch(`${API}/shadow/lists/${id}`, {
      method: "DELETE", credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    await load();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
          <ListIcon className="mx-auto h-10 w-10 text-cyan-300/80" />
          <h1 className="mt-4 text-3xl font-black tracking-tight">{tFases(lang, "ls_title")}</h1>
          <p className="mt-2 text-sm text-white/55">{tFases(lang, "ls_login_sub")}</p>
          <button onClick={openLogin} className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black" data-testid="lists-login-btn">
            {tFases(lang, "common_login")}
          </button>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="shadow-lists-page">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/shadow" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 hover:text-cyan-300" data-testid="lists-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {tFases(lang, "common_back")}
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300/80">{tFases(lang, "ls_eyebrow")}</p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">{tFases(lang, "ls_h1")}</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/55">
              {tFases(lang, "ls_h1_sub")}
              {!isPro && <span className="ml-1 text-amber-200/80">{tFases(lang, "ls_quota_hint", { n: quota.limit })}</span>}
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            disabled={quota.used >= quota.limit}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-500 px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.16em] text-black hover:-translate-y-0.5 transition-all disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="lists-new-btn"
          >
            <Plus className="h-3.5 w-3.5" /> {tFases(lang, "ls_new_btn")}
          </button>
        </div>

        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
          {tFases(lang, "ls_used", { u: quota.used, l: quota.limit })}
        </p>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /> {tFases(lang, "common_loading")}</div>
        ) : lists.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.025] p-10 text-center backdrop-blur">
            <ListIcon className="mx-auto h-9 w-9 text-white/35" />
            <p className="mt-3 text-sm text-white/55">{tFases(lang, "ls_empty")}</p>
          </div>
        ) : (
          <ul className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
            {lists.map((l) => {
              const Icon = l.visibility === "private" ? Lock : Globe;
              return (
                <li
                  key={l.id}
                  className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur transition-all hover:border-cyan-400/30 hover:bg-cyan-400/[0.03]"
                  data-testid={`list-row-${l.id}`}
                >
                  <Link to={`/shadow/listas/${l.id}`} className="block p-5">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="truncate text-base font-bold text-white">{l.name}</h3>
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-white/55"
                        title={l.visibility === "private" ? tFases(lang, "ls_vis_private") : tFases(lang, "ls_vis_public")}
                      >
                        <Icon className="h-2.5 w-2.5" /> {l.visibility === "private" ? tFases(lang, "ls_vis_private") : tFases(lang, "ls_vis_public")}
                      </span>
                    </div>
                    {l.description && (
                      <p className="mt-2 line-clamp-2 text-xs text-white/55">{l.description}</p>
                    )}
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-300/70">
                      {tFases(lang, "ls_members_count", { n: l.member_count })}
                    </p>
                  </Link>
                  <button
                    onClick={() => remove(l.id)}
                    className="absolute right-3 top-3 rounded-full border border-white/10 p-1.5 text-white/30 transition-colors hover:border-rose-400/40 hover:text-rose-300"
                    aria-label="Eliminar"
                    data-testid={`list-delete-${l.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {showForm && (
        <div className="fixed inset-0 z-[9100] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-md" onClick={() => setShowForm(false)} data-testid="lists-new-modal">
          <form onSubmit={create} onClick={(e) => e.stopPropagation()} className="w-full max-w-md overflow-hidden rounded-2xl border border-cyan-400/30 bg-zinc-950 p-6 shadow-[0_0_60px_rgba(34,211,238,0.18)]">
            <h3 className="text-lg font-black">{tFases(lang, "ls_modal_title")}</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "ls_f_name")}</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.slice(0, 60) })}
                  maxLength={60}
                  placeholder={tFases(lang, "ls_f_name_ph")}
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50"
                  data-testid="lists-form-name"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "ls_f_desc")}</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 240) })}
                  rows={2}
                  maxLength={240}
                  className="mt-1.5 w-full resize-none rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50"
                  data-testid="lists-form-desc"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "ls_f_vis")}</label>
                <div className="mt-1.5 flex gap-1">
                  {[
                    { id: "public", label: tFases(lang, "ls_vis_public"), icon: Globe, color: "#10b981" },
                    { id: "private", label: tFases(lang, "ls_vis_private"), icon: Lock, color: "#94a3b8" },
                  ].map((v) => (
                    <button
                      type="button"
                      key={v.id}
                      onClick={() => setForm({ ...form, visibility: v.id })}
                      data-testid={`lists-form-vis-${v.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-[11px] font-bold uppercase tracking-wider"
                      style={{
                        borderColor: form.visibility === v.id ? v.color : "rgba(255,255,255,0.1)",
                        background: form.visibility === v.id ? `${v.color}1f` : "transparent",
                        color: form.visibility === v.id ? v.color : "rgba(255,255,255,0.55)",
                      }}
                    >
                      <v.icon className="h-3.5 w-3.5" /> {v.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {err && (
              <p className="mt-3 flex items-center gap-1 text-xs text-rose-300" data-testid="lists-form-error">
                <AlertCircle className="h-3 w-3" /> {err}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-full border border-white/15 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white/70">{tFases(lang, "common_cancel")}</button>
              <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-5 py-2 text-[11px] font-black uppercase tracking-wider text-black disabled:opacity-60" data-testid="lists-form-submit">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                {tFases(lang, "ls_create")}
              </button>
            </div>
          </form>
        </div>
      )}

      <Footer />
    </div>
  );
}
